import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { safeJsonLd } from "@/lib/safeJsonLd";

const FAQSection = () => {
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);
  const lang = i18n.language?.startsWith("en") ? "en" : "it";

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/content/faqs");
        const c = res.data?.content || res.data;
        setData(c);
      } catch (_) {
        setData(null);
      }
    };
    load();
  }, []);

  const items = (data?.items || []).filter((it) => it && (it.question_it || it.question_en));
  if (!data || data.is_visible === false || items.length === 0) return null;

  const title = lang === "en" ? data.title_en || data.title_it : data.title_it;
  const subtitle = lang === "en" ? data.subtitle_en || data.subtitle_it : data.subtitle_it;

  // JSON-LD FAQPage structured data for SEO (always in Italian, since site is primarily IT)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((it) => ({
      "@type": "Question",
      "name": it.question_it || it.question_en,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": it.answer_it || it.answer_en,
      },
    })),
  };

  return (
    <section data-reveal className="py-16 md:py-24 bg-background" data-testid="home-faq-section" id="faq">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd) }}
      />
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 md:mb-14">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#7e823a] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="home-faq-title"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-base md:text-lg text-[#5a5a5a] max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {items.map((it, idx) => {
            const q = lang === "en" ? it.question_en || it.question_it : it.question_it;
            const a = lang === "en" ? it.answer_en || it.answer_it : it.answer_it;
            const isOpen = openIdx === idx;
            return (
              <article
                key={idx}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden transition-shadow hover:shadow-md"
                data-testid={`home-faq-item-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                  aria-expanded={isOpen}
                  data-testid={`home-faq-toggle-${idx}`}
                >
                  <h3
                    itemProp="name"
                    className="text-base md:text-lg font-semibold text-[#2C2C2C] flex-1"
                  >
                    {q}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-[#7e823a] flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && a && (
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className="px-5 md:px-6 pb-5 md:pb-6 -mt-1"
                  >
                    <p
                      itemProp="text"
                      className="text-sm md:text-base text-[#5a5a5a] leading-relaxed whitespace-pre-line"
                    >
                      {a}
                    </p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
